import { MobileUserOrderModal } from 'components/MobileUserOrderModalV3'
import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'store'
import { useAccountInfo, useIsLogin } from 'store/account/hooks'
import { useHideOtherPairs } from 'store/orderbook/hooks'
import { useCurrentSpotSymbol, useSpotClosedOrders } from 'store/spot/hooks'
import { Equal } from '../../../../utils/equal'
import {
  HistoryOrdersWrap,
  CoinData,
  CoinSize,
  CoinIcon,
  CoinName,
  StyledColumn,
  SymbolLink,
  TriggerDone,
  StyledEndItem,
} from '../styles'
import {
  orderModalAtom,
  OrderModalType,
} from 'components/MobileUserOrderModalV3'
import { DateFormatter } from 'utils/datetime'
import { useSpotSymbols } from 'store/spot/hooks'
import { findSymbolById } from 'utils/symbols'
import { ReactComponent as ArrowIconSvg } from 'assets/orders/symbol_arrow.svg'
import { useMarketNavigate } from 'hooks/useMarketNavigate'
import { SpotOrderRecord } from 'api/v1/spot'
import { TrendColorizer } from 'components/TrendColorizer'
import { OrderDirection } from 'store/spot/types'
import { toFixed, isZero } from 'utils/number'
import { OrderStatus } from 'components/OrderStatus'
import { TableList } from 'components/TableList'
import useWindow from 'hooks/useWindow'
import { useSpotCurrencies } from 'store/market/hooks'
import { Flex } from 'styles'
import { calcOrderFilledSize } from 'hooks/useSizeCalculations'
import { useSetAtom } from 'jotai'
import { getSpotClosedOrdersAction } from 'store/spot/actions/getSpotClosedOrdersActions'
import { SideSelectPopup } from '../components/SideSelectPopup'
import { StatusSelectPopup } from '../components/StatusSelectPopup'
import {
  SpotOrderDirectionFilterEnum,
  SpotOrderStatusFilterEnum,
} from 'store/spot/types'
import { SpotOrderStatus } from 'api/v1/spot'
import { customOrderHistoryRowStyle } from 'views/History/Orders/SpotOrderHistory'
import { StringFormatter } from 'utils/StringFormatter'
import MobileTableList from 'components/TableList/MobileTableList'
import MobileRowItem from './MobileRowItem'

const INIT_ORDER_STATUS = [
  SpotOrderStatusFilterEnum.All,
  SpotOrderStatusFilterEnum.FAILED,
  SpotOrderStatusFilterEnum.FULLY_FILLED,
  SpotOrderStatusFilterEnum.PARTIAL_FILLED,
  SpotOrderStatusFilterEnum.PARTIAL_CANCELLED,
  SpotOrderStatusFilterEnum.FULLY_CANCELLED,
]

export const OrderHistoryView = memo(() => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { accountId = 0 } = useAccountInfo()
  const isLogin = useIsLogin()
  const currentSpotSymbol = useCurrentSpotSymbol()
  const { name } = currentSpotSymbol ?? {}
  const hideOtherPairs = useHideOtherPairs()
  const { list, isLastPage, offsetId } = useSpotClosedOrders()
  const spotSymbols = useSpotSymbols()
  const { navigateToSpot } = useMarketNavigate()
  const { isMobile } = useWindow()
  const tokenList = useSpotCurrencies()
  const setSelectedOrder = useSetAtom(orderModalAtom)
  const [fetchParams, setFetchParams] = useState<any>({
    direction: SpotOrderDirectionFilterEnum.All,
    status: SpotOrderStatusFilterEnum.All,
    offsetId: 0,
  })
  const [filterStatus, setFilterStatus] =
    useState<Array<any>>(INIT_ORDER_STATUS)

  useEffect(() => {
    let str = ''
    // @ts-ignore
    if (filterStatus.indexOf('') < 0) {
      str = filterStatus.reduce((prev, curr, index) => {
        prev = `${prev}${index === 0 ? '' : ','}${curr}`
        return prev
      }, '')
    }

    setFetchParams((prev: any) => {
      return Object.assign({}, { ...prev }, { status: str })
    })
  }, [filterStatus])

  useEffect(() => {
    if (isLogin) {
      dispatch(
        getSpotClosedOrdersAction({
          accountId,
          body: {
            ...fetchParams,
          },
        }),
      )
    }
  }, [fetchParams, isLogin, accountId])

  const renderList = useMemo(() => {
    if (hideOtherPairs && !!name)
      return list.filter((v) => Equal.upperCase(v.symbol, name))
    return list
  }, [list, hideOtherPairs, name])

  const getSymbolInfo = (symbolId: number) => {
    const symbol = findSymbolById(spotSymbols, symbolId)
    const {
      baseCurrency = '',
      quoteCurrency = '',
      baseScale = 0,
      quoteScale = 0,
    } = symbol ?? {}

    return {
      baseCurrency,
      quoteCurrency,
      baseScale,
      quoteScale,
    }
  }

  const getFilledSizeQuantity = (row: SpotOrderRecord) => {
    const { symbolId, quantity = 0, unfilledQuantity = 0 } = row
    const symbol = getSymbolInfo(symbolId)
    const { baseScale } = symbol
    const filledSize = calcOrderFilledSize(quantity, unfilledQuantity)
    const fixedQuantity = toFixed(quantity, baseScale)
    const fixedFilledSize = toFixed(filledSize, baseScale)
    return {
      fixedFilledSize,
      fixedQuantity,
    }
  }

  const onSelectDirectionFilter = (value: SpotOrderDirectionFilterEnum) => {
    setFetchParams((prev: any) => {
      return Object.assign({}, { ...prev }, { direction: value })
    })
  }

  const onSelectStatusFilter = ({
    checked,
    key,
  }: {
    checked: boolean
    key: SpotOrderStatusFilterEnum
  }) => {
    if (key === SpotOrderStatusFilterEnum.All) {
      if (checked) {
        setFilterStatus(INIT_ORDER_STATUS)
      }
    } else {
      if (!checked) {
        setFilterStatus((prev) => {
          const curr = [...prev]
            .filter((d) => d !== key)
            .filter((d) => d !== SpotOrderStatusFilterEnum.All)
          return curr
        })
      } else {
        setFilterStatus((prev) => {
          const curr = [...prev]
          if (curr.length + 1 === 5) {
            return INIT_ORDER_STATUS
          } else {
            return [...prev, key]
          }
        })
      }
    }
  }

  const filterRenderList = useMemo(() => {
    if (renderList?.length > 0) {
      let list = renderList
      const { direction } = fetchParams

      if (direction !== SpotOrderDirectionFilterEnum.All) {
        list = list.filter((d) => d.direction === direction)
      }

      if (filterStatus.indexOf(SpotOrderStatusFilterEnum.All) < 0) {
        list = list.filter((d) => {
          const { status } = d
          return filterStatus.indexOf(status) > -1
        })
      }

      return list
    }
    return renderList
  }, [renderList, filterStatus, fetchParams])

  const columns = [
    {
      key: 'date',
      title: 'Date',
      render: (row: SpotOrderRecord) => {
        const { createdAt } = row
        const datetime = DateFormatter.toISODateTime(createdAt)
        return <span style={{ color: '#fff' }}>{datetime}</span>
      },
      width: '13%',
    },

    {
      key: 'symbol',
      title: 'Pair',
      render: (row: SpotOrderRecord) => {
        const { symbolId } = row
        const symbol = getSymbolInfo(symbolId)
        const { baseCurrency, quoteCurrency } = symbol ?? {}

        return (
          <SymbolLink
            sx={{ cursor: 'pointer' }}
            onClick={() => navigateToSpot(baseCurrency, quoteCurrency)}
          >
            {baseCurrency}/{quoteCurrency}
            <ArrowIconSvg />
          </SymbolLink>
        )
      },
      width: '12%',
    },
    {
      key: 'type',
      title: 'Type',
      render: (row: SpotOrderRecord) => {
        const { type } = row
        return <>{StringFormatter.startCase(type)}</>
      },
      width: '10%',
    },
    {
      key: 'side',
      titleRender: () => {
        return (
          <SideSelectPopup
            onSelected={onSelectDirectionFilter}
            filterSide={fetchParams?.filterSide}
          />
        )
      },
      render: (row: SpotOrderRecord) => {
        const { direction } = row
        return (
          <TrendColorizer
            trend={direction === OrderDirection.LONG}
            value={
              direction === OrderDirection.LONG
                ? t('user-order-table-content-buy')
                : t('user-order-table-content-sell')
            }
          />
        )
      },
      width: '10%',
    },
    {
      key: 'filled',
      title: t('history-orders-table-filled'),
      render: (row: SpotOrderRecord) => {
        const { symbolId } = row
        const { baseCurrency } = getSymbolInfo(symbolId)
        const { fixedFilledSize, fixedQuantity } = getFilledSizeQuantity(row)
        return (
          <>
            <span>{fixedFilledSize}</span>
            <span>
              /{fixedQuantity} {baseCurrency}
            </span>
          </>
        )
      },
      width: '14%',
    },
    {
      key: 'avg-price',
      title: t('history-orders-table-avg-price'),
      render: (row: SpotOrderRecord) => {
        const { symbolId, fillPrice } = row
        const { quoteScale, quoteCurrency } = getSymbolInfo(symbolId)
        return (
          <>
            {fillPrice > 0
              ? toFixed(fillPrice, quoteScale) + ' ' + quoteCurrency
              : '-'}
          </>
        )
      },
      width: '13%',
    },
    {
      key: 'price',
      title: t('user-order-table-header-price'),
      render: (row: SpotOrderRecord) => {
        const { symbolId, price } = row
        const { quoteScale, quoteCurrency } = getSymbolInfo(symbolId)
        const fixedPrice = toFixed(price, quoteScale)
        return (
          <>
            {fixedPrice} {quoteCurrency}
          </>
        )
      },
      width: '13%',
    },
    {
      key: 'Trigger Conditions',
      title: t('user-order-table-header-trigger'),
      render: (row: SpotOrderRecord) => {
        const {
          triggerOn,
          triggerDirection = OrderDirection.LONG,
          status,
        } = row
        return (
          <>
            {isZero(triggerOn) ? (
              '-'
            ) : (
              <>
                Last Price{' '}
                {triggerDirection === OrderDirection.LONG ? '>=' : '<='}{' '}
                {triggerOn}
                {status === SpotOrderStatus.PENDING && <TriggerDone />}
              </>
            )}
          </>
        )
      },
      width: '14%',
    },
    {
      key: 'status',
      titleRender: () => {
        return (
          <StyledEndItem>
            <StatusSelectPopup
              onSelected={onSelectStatusFilter}
              filterStatus={filterStatus}
            />
          </StyledEndItem>
        )
      },
      render: (row: SpotOrderRecord) => {
        const { status } = row
        return (
          <StyledEndItem>
            <OrderStatus status={status} />
          </StyledEndItem>
        )
      },
      width: '10%',
    },
  ]

  const mobileColumns = [
    {
      key: 'mobile_symbol',
      title: `${t('user-order-table-header-size')}/${t(
        'user-order-table-header-market',
      )}`,
      render: (row: SpotOrderRecord) => {
        const { symbolId, direction, symbol: symbolName } = row
        const symbol = getSymbolInfo(symbolId)
        const { baseCurrency } = symbol
        const token = tokenList.find((t) => baseCurrency === t.name)
        const { tokenIconUrl = '' } = token ?? {}
        const { fixedQuantity } = getFilledSizeQuantity(row)
        return (
          <Flex>
            <CoinIcon>
              {tokenIconUrl && <img src={tokenIconUrl} alt={baseCurrency} />}
            </CoinIcon>
            <CoinData>
              <CoinSize
                className={direction === OrderDirection.LONG ? 'buy' : 'sell'}
              >
                {fixedQuantity}
              </CoinSize>
              <CoinName>{symbolName}</CoinName>
            </CoinData>
          </Flex>
        )
      },
    },
    {
      key: 'mobile_price',
      titleRender: () => {
        return (
          <StyledColumn style={{ alignItems: 'flex-start' }}>
            {t('user-order-table-header-price')}/
            {t('user-order-table-header-amount')}
          </StyledColumn>
        )
      },
      render: (row: SpotOrderRecord) => {
        const { symbolId, price } = row
        const { quoteScale, baseCurrency } = getSymbolInfo(symbolId)
        const fixedPrice = toFixed(price, quoteScale)
        const { fixedQuantity } = getFilledSizeQuantity(row)
        return (
          <StyledColumn style={{ alignItems: 'flex-start' }}>
            <span className="title">{fixedPrice}</span>
            <span className="sub">
              {fixedQuantity} {baseCurrency}
            </span>
          </StyledColumn>
        )
      },
    },
    {
      key: 'mobile_filled',
      titleRender: () => {
        return (
          <StyledColumn style={{ alignItems: 'flex-end' }}>
            {t('user-order-table-header-filled')}/
            {t('user-order-table-header-size')}
          </StyledColumn>
        )
      },
      render: (row: SpotOrderRecord) => {
        const { fixedQuantity, fixedFilledSize } = getFilledSizeQuantity(row)
        return (
          <StyledColumn style={{ alignItems: 'flex-end' }}>
            <span className="title">{fixedFilledSize}</span>
            <span className="sub">{fixedQuantity}</span>
          </StyledColumn>
        )
      },
    },
  ]

  const onScrollFetch = () => {
    if (isLogin) {
      dispatch(
        getSpotClosedOrdersAction({
          accountId,
          body: {
            ...fetchParams,
            offsetId: offsetId || 0,
          },
        }),
      )
    }
  }

  return (
    <HistoryOrdersWrap id="scrollContent">
      {isMobile ? (
        <MobileTableList
          dataSource={filterRenderList}
          hasMore={!isLastPage}
          onScrollEnd={() => {
            if (isLastPage) return
            onScrollFetch()
          }}
          renderItem={(row) => {
            return <MobileRowItem row={row}></MobileRowItem>
          }}
        />
      ) : (
        <TableList
          hasMore={!isLastPage}
          onScrollEnd={() => {
            if (isLastPage) return
            onScrollFetch()
          }}
          rowStyle={customOrderHistoryRowStyle}
          columns={columns}
          dataSource={filterRenderList}
        />
      )}
      <MobileUserOrderModal />
    </HistoryOrdersWrap>
  )
})
